import {Plugins, withSystem} from '@packages/systems/wfdl';
import {
  TTypeConstructor,
  TNumber,
  TText,
  TUnion,
  functionOf,
  ECase,
} from '@packages/systems/wfdl-ast';
import {
  qualify,
  pluginName,
  typeName,
  identifier,
} from '@packages/systems/wfdl-Qualified';
import {call1, _$, tyfun, func, $} from '@packages/utilities/wfdl-helpers';
import {
  TPredicate,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {OptionalNumber} from './OptionalNumber';
import {operand} from '@packages/systems/plugins/ConditionsOperand';

const numberToNumberToBoolean = functionOf(
  // this argument represents the condition value which should be bindable
  withSystem({isBindable: true}, TNumber),
  TPredicate(TNumber)
);

export const TOptionalNumber = TTypeConstructor(
  qualify(pluginName('Number'), typeName('Optional Number'))
);

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    // TODO: Determine if this can be removed with Logic code
    'Optional Number': OptionalNumber,
  },
  values: {
    // TODO: Determine if this can be removed with Logic code
    'Parse Number': {
      envType: {
        exported: 'hidden',
        dataType: functionOf(
          TText,
          TUnion({
            Error: TText,
            Ok: TNumber,
          })
        ),
      },
      value: (value: string) => {
        const parsedNumber = parseFloat(value);
        return Number.isFinite(parsedNumber)
          ? {tag: 'Ok', val: parsedNumber}
          : {tag: 'Error', val: 'Must be a valid number.'};
      },
    },

    // Exported as hidden since we already have two other number to text
    // implicit functions. Ideally, we would consolidate to a single
    // implementation based in the Number plugin.
    // TODO: Determine if this can be removed with Logic code
    'To Text': {
      envType: {
        exported: 'hidden',
        dataType: functionOf(TNumber, TText),
      },
      value: String,
    },

    // TODO: Determine if this can be removed with Logic code
    'Option Number to Text': {
      envType: {
        dataType: tyfun([TText, TOptionalNumber], TText),
        exported: 'hidden',
      },
      expression: func(
        [identifier('fallback text'), identifier('optional number')],
        ECase({
          value: $('optional number'),
          cases: {
            Some: _$('Number', 'To Text'),
            None: func([identifier('_')], $('fallback text')),
          },
        })
      ),
    },

    Equals: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is'}, numberToNumberToBoolean),
      },
      value: (num1: number) => (num2: number) => toBooleanTag(num1 === num2),
    },

    'Does not equal': {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is not'}, numberToNumberToBoolean),
      },
      value: (num1: number) => (num2: number) => toBooleanTag(num1 !== num2),
    },

    'Is greater than': {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is greater than'},
          numberToNumberToBoolean
        ),
      },
      value: (num1: number) => (num2: number) => toBooleanTag(num2 > num1),
    },

    'Is greater than or equal': {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is greater or equal'},
          numberToNumberToBoolean
        ),
      },
      value: (num1: number) => (num2: number) => toBooleanTag(num2 >= num1),
    },

    'Is less than': {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is less than'}, numberToNumberToBoolean),
      },
      value: (num1: number) => (num2: number) => toBooleanTag(num2 < num1),
    },

    'Is less than or equal': {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is less or equal'},
          numberToNumberToBoolean
        ),
      },
      value: (num1: number) => (num2: number) => toBooleanTag(num2 <= num1),
    },

    Condition: operand(TNumber),
  },
});

export const parseNumberRef = _$('Number', 'Parse Number');
export const parseNumber = call1(parseNumberRef);
